import ArrowUp from "@/Icons/ArrowUp";
import { isCarrotAdmin } from "@/Utils/authUtils";
import type { PresetKey } from "@/components/v2/DatePicker/constants";
import type { KpiNumberData } from "@/components/v2/KpiNumbers";
import {
  BottomTextView,
  comparisonKeys,
  getBottomTextView,
  getKpiValue,
} from "@/components/v2/KpiNumbers/utils";
import type { WasteTypeConfig } from "@/pages/configuration/useConfigService";
import { getContaminationRate } from "@/pages/contamination/utils";

import { Link } from "react-router-dom";

import { sum } from "lodash";
import type { GetTerminalResponse } from "server/dwhApi/terminals/models.js";
import type { Terminal } from "server/dwhApi/terminals/types.js";

const getCo2Value = ({
  terminal,
  wasteTypeConfig,
}: {
  terminal: Terminal;
  wasteTypeConfig: WasteTypeConfig[];
}) => {
  return sum(
    terminal.wasteTypes.map((wt) => {
      const wasteConfig = wasteTypeConfig.find(
        (wtc) => wtc.wasteTypeCode === wt.code,
      );

      return wt.weight.quantity * (wasteConfig?.co2factor || 0);
    }),
  );
};

type Props = {
  currentData?: GetTerminalResponse;
  comparisonData?: GetTerminalResponse;
  datePreset?: PresetKey;
  presetHasComparison: boolean;
  t: (key: string) => string;
  wasteTypeConfig: WasteTypeConfig[];
  sitePath: string;
  hasContaminationEnabled: boolean;
};

export const getKpiNumbersData = ({
  currentData,
  comparisonData,
  datePreset,
  presetHasComparison,
  t,
  wasteTypeConfig,
  sitePath,
  hasContaminationEnabled,
}: Props): KpiNumberData[] => {
  if (!currentData) {
    return [];
  }

  const hasNoComparisonData =
    !presetHasComparison || !comparisonData || !currentData;

  const comparisonKey = t(comparisonKeys[datePreset || ""]);

  const totalCo2 = getCo2Value({
    terminal: currentData.terminal,
    wasteTypeConfig,
  });

  return [
    {
      id: "recyclingRate",
      title: t("homePage:kpiRecyclingRate"),
      getValue: () =>
        getKpiValue((currentData?.terminal.sortingDegree || 0) * 100),
      valueSuffix: "%",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: (currentData?.terminal.sortingDegree || 0) * 100,
          comparisonValue: (comparisonData?.terminal.sortingDegree || 0) * 100,
          comparisonKey,
          higherBetter: true,
          differenceSuffix: t("common:percentagePoints"),
        }),
    },
    {
      id: "totalWaste",
      title: t("homePage:kpiTotalWaste"),
      getValue: () =>
        getKpiValue(currentData?.terminal.totalWasteWeight.quantity),
      valueSuffix: "kg",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: currentData?.terminal.totalWasteWeight.quantity || 0,
          comparisonValue:
            comparisonData?.terminal.totalWasteWeight.quantity || 0,
          comparisonKey,
          differenceSuffix: " kg",
        }),
    },
    {
      id: "generalWaste",
      title: t("homePage:kpiGeneralWaste"),
      getValue: () =>
        getKpiValue(currentData?.terminal.totalResidualWasteWeight.quantity),
      valueSuffix: "kg",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            currentData?.terminal.totalResidualWasteWeight.quantity || 0,
          comparisonValue:
            comparisonData?.terminal.totalResidualWasteWeight.quantity || 0,
          comparisonKey,
          differenceSuffix: " kg",
        }),
    },
    hasContaminationEnabled && {
      id: "contamination",
      title: t("homePage:kpiContamination"),
      getValue: () => getKpiValue(getContaminationRate(currentData.terminal)),
      valueSuffix: "%",

      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: getContaminationRate(currentData.terminal),
          comparisonValue: getContaminationRate(comparisonData?.terminal),
          comparisonKey,
          differenceSuffix: t("common:percentagePoints"),
        }),
    },
    {
      id: "activation",
      title: t("homePage:kpiActivation"),
      getValue: () => currentData?.terminal.totalCustomers || 0,
      valueSuffix: `/${currentData.terminal.totalCustomersAcrossTerminal}`,
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: currentData?.terminal.totalCustomers || 0,
          comparisonValue: comparisonData?.terminal.totalCustomers || 0,
          comparisonKey,
          higherBetter: true,
          differenceSuffix: "",
        }),
    },
    {
      id: "co2Emissions",
      title: t("homePage:co2Emissions"),
      getValue: () => {
        if (!currentData) return 0;

        return totalCo2 ? getKpiValue(totalCo2) : "-";
      },
      valueSuffix: " t CO₂e",
      getBottomText: () =>
        totalCo2 === 0 ? (
          <div className="mt-[12px]">
            <Link
              to={{
                pathname: `${sitePath}/settings`,
                search: `?tab=${isCarrotAdmin() ? 5 : 2}`,
              }}
              className="flex text-[12.5px] font-semibold text-[#1D1C1C] underline underline-offset-2"
            >
              {t("homePage:co2GoToSettingsButton")}
              <div className="ml-1 rotate-90">
                <ArrowUp />
              </div>
            </Link>
          </div>
        ) : (
          <BottomTextView
            isPositive={false}
            bottomText={t("homePage:kpiCo2EmissionsBottomText")}
          />
        ),
      dataNotAvailable: totalCo2 === 0,
      dataNotAvailableTextKey: "homePage:noCo2DataText",
    },
  ].filter(Boolean) as KpiNumberData[];
};
